import { Fragment, useState } from "react";
import { Link } from "wouter";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { WidgetPanel } from "@/components/dashboard-grid/WidgetPanel";
import { groupProspects, lastTouchLabel, type PlayProspect } from "@shared/dashboardOrigination";
import { prospectStageIndex } from "@shared/prospectConstants";
import { StagePips } from "./StagePips";

type Filter = "all" | "mine" | "starred" | "mva";
const firstName = (full: string | null) => full?.split(" ")[0] ?? "—";

export function ProspectsInPlay({ rows, canStar, meName }: { rows: PlayProspect[]; canStar: boolean; meName: string | null }) {
  const [filter, setFilter] = useState<Filter>("all");
  const now = new Date();
  const shown = rows.filter((p) =>
    filter === "all" ? true : filter === "mine" ? !!meName && p.ownerName === meName : filter === "starred" ? !!p.starred : !!p.mvaStatus);
  const chips: [Filter, string][] = [["all", "All"], ["mine", "Mine"], ...(canStar ? [["starred", "Starred"] as [Filter, string]] : []), ["mva", "MVA open"]];

  return (
    <WidgetPanel
      title="Prospects in play"
      meta={shown.length}
      action={
        <>
          <div className="flex gap-1">
            {chips.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                className={cn("text-[11px] px-2 py-0.5 rounded-full border", filter === k ? "bg-sidebar text-white border-sidebar" : "border-border text-muted-foreground hover:text-foreground")}>
                {label}
              </button>
            ))}
          </div>
          <Link href="/prospects" className="text-xs font-medium text-primary-text hover:underline">All prospects</Link>
        </>
      }
    >
      {shown.length === 0 ? (
        <p className="px-3 py-4 text-sm text-muted-foreground">No prospects in play{filter === "all" ? "" : " for this filter"}.</p>
      ) : (
        <div className="@container">
        <table className="w-full table-fixed text-[0.8125rem]">
          <thead className="sticky top-0 z-10 bg-card text-[11px] text-muted-foreground">
            <tr className="border-b border-border/60">
              {canStar && <th className="w-6" />}
              <th className="text-left font-medium px-2 py-1.5">Company</th>
              <th className="text-left font-medium px-2 w-[4.5rem] @2xl:w-44">Stage</th>
              <th className="text-left font-medium px-2 w-[30%]">MVA</th>
              <th className="hidden @xl:table-cell text-left font-medium px-2 w-16">Owner</th>
              <th className="text-right font-medium px-2 w-16">Last touch</th>
            </tr>
          </thead>
          <tbody>
            {groupProspects(shown).map((g) => (
              <Fragment key={g.group}>
                <tr>
                  <td colSpan={canStar ? 6 : 5} className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/30">{g.label}</td>
                </tr>
                {g.rows.map((p) => {
                  const touch = lastTouchLabel(p.lastTouchAt, now);
                  const idx = prospectStageIndex(p.stage);
                  const waiting = p.mvaStatus && p.mvaStatus !== "In progress" && p.mvaStatus !== "In review";
                  return (
                    <tr key={p.id} className="border-b border-border/40 hover:bg-muted/40">
                      {canStar && <td className="text-center text-amber-600">{p.starred ? "★" : ""}</td>}
                      <td className="px-2 py-1 truncate font-medium">
                        <Link href={`/prospects/${p.id}`} className="hover:underline">{p.name}</Link>
                        {p.sourceType === "coi_referral" && <span className="ml-1.5 text-[10px] font-semibold text-primary-text border border-primary/40 rounded px-1">COI</span>}
                      </td>
                      <td className="px-2 truncate">
                        <StagePips filled={idx + 1} label={p.stage ?? "No stage yet"} />
                        <span className="hidden @2xl:inline text-xs text-muted-foreground">{p.stage ?? "no stage"}</span>
                      </td>
                      <td className="px-2 truncate text-xs">
                        {p.mvaStatus ? (
                          <><span className={cn("font-semibold", waiting && "text-amber-700 dark:text-amber-400")}>{p.mvaStatus}</span>{p.mvaAssignee ? ` · ${firstName(p.mvaAssignee)}` : ""}</>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="hidden @xl:table-cell px-2 truncate text-xs text-muted-foreground">{firstName(p.ownerName)}</td>
                      <td className="px-2 text-right text-xs tabular-nums">
                        {touch.never ? <span className="italic text-muted-foreground">never</span>
                          : touch.stale ? <span className="text-amber-700 dark:text-amber-400 inline-flex items-center gap-1" title="No touch in over 30 days"><Clock className="w-3 h-3" />{touch.text}</span>
                          : touch.text}
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </WidgetPanel>
  );
}
